import { useCallback, useState } from "react";
import { Button, Flex, Separator, Spinner } from "@radix-ui/themes";
import { Pencil2Icon } from "@radix-ui/react-icons";
import { FieldGroup } from "@/v2/components/fields/field-group/FieldGroup";
import { InlineTextField } from "@/components/shared/inputs/inilie-text-field/InlineTextField";
import {
  CreateSampleConfigurationsParams,
  CreateSampleFormParams,
  CreateSampleFormQuestion,
  QaRules,
} from "@/v2/entities/sampling/Sampling.api";
import { TargetStep } from "./TargetStep";
import { EvaluateStep, createQuestion } from "./EvaluateStep";
import { ValidateStep, createSampleConfiguration } from "./ValidateStep";

const defaultValues: CreateSampleFormParams & CreateSampleConfigurationsParams =
{
  name: "Awesome Scorecard",
  questions: [],
  configurations: [createSampleConfiguration()],
  qaRules: null,
};

interface SampleFormErrors {
  name?: string;
  qaRules?: string;
  questions?: string;
}

function isEmptyQaRules(qaRules: QaRules): boolean {
  if (!qaRules) return true;
  if (Object.keys(qaRules).length === 0) return true;

  if ("and" in qaRules && Array.isArray(qaRules.and)) {
    return qaRules.and.length === 0;
  }

  return false;
}

function validateSampleForm(form: CreateSampleFormParams): SampleFormErrors {
  const errors: SampleFormErrors = {};

  if (form.name == null || form.name === "") {
    errors.name = "Scorecard name cannot be empty";
  }

  if (!!form.qaRules) {
    if (isEmptyQaRules(form.qaRules)) {
      errors.qaRules = "Please add at least one team or channel";
    }
  }

  if (form.questions.length === 0) {
    errors.questions = "Please add at least one question";
  }

  return errors;
}

interface SampleFormProps {
  initialValues?: CreateSampleFormParams & CreateSampleConfigurationsParams;
  defaultNewQuestion?: CreateSampleFormQuestion;
  mode?: "create" | "update";
  submitLabel?: string;
  loading?: boolean;
  onSubmit: (
    data: CreateSampleFormParams & CreateSampleConfigurationsParams
  ) => Promise<void>;
}

export function SampleForm({
  initialValues = defaultValues,
  defaultNewQuestion = createQuestion(),
  submitLabel = "Create Scorecard",
  mode = "create",
  loading = false,
  onSubmit,
}: SampleFormProps) {
  const [formData, setFormData] = useState<
    CreateSampleFormParams & CreateSampleConfigurationsParams
  >(initialValues);
  const [errors, setErrors] = useState<SampleFormErrors>({});
  const [questionInEdit, setQuestionInEdit] = useState<string | undefined>(
    mode === "create" ? defaultNewQuestion.key : undefined
  );

  const handleSampleFormChange = useCallback(
    (data: CreateSampleFormParams) => {
      setFormData({ ...formData, ...data });
    },
    [formData]
  );

  const handleSampleConfigurationsChange = useCallback(
    (data: CreateSampleConfigurationsParams) => {
      setFormData({ ...formData, ...data });
    },
    [formData]
  );

  const handleSubmit = useCallback(async () => {
    if (questionInEdit != null) {
      return;
    }

    setErrors({});
    const errors = validateSampleForm(formData);

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    onSubmit(formData);
  }, [onSubmit, formData, questionInEdit]);

  return (
    <Flex direction={"column"}>
      <Button
        variant="solid"
        color="gray"
        size="2"
        highContrast
        disabled={loading || questionInEdit != null}
        onClick={handleSubmit}
        className="fixed top-[10px] right-[48px]"
      >
        <Spinner loading={loading} />

        {submitLabel}
      </Button>

      <Flex direction={"column"} gap={"8"}>
        <FieldGroup label="Scorecard Name" noSeparator error={errors.name}>
          <InlineTextField
            size={"5"}
            value={formData.name}
            onChange={(value) => setFormData({ ...formData, name: value })}
            placeholder="Scorecard Name"
            icon={<Pencil2Icon width={24} height={24} className="ml-2" />}
          />
        </FieldGroup>

        <TargetStep
          value={formData}
          onChange={handleSampleFormChange}
          error={errors.qaRules}
        />

        <Separator orientation="horizontal" size="4" />

        <EvaluateStep
          value={formData}
          questionInEdit={questionInEdit}
          error={errors.questions}
          setQuestionInEdit={setQuestionInEdit}
          onChange={handleSampleFormChange}
          defaultNewQuestion={
            mode === "create" ? defaultNewQuestion : undefined
          }
        />

        <Separator orientation="horizontal" size="4" />

        <ValidateStep
          value={formData}
          onChange={handleSampleConfigurationsChange}
        />
      </Flex>
    </Flex>
  );
}
